import React, { FunctionComponent, useContext, useEffect, useMemo } from "react";
import { Theme, makeStyles, Grid, useMediaQuery } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { NutritionContext } from "../../context/nutrition-context";
import {
  GeneDataGroupType,
  GenePopulationResponse,
  useGenePopulationsByReportLazyQuery,
} from "../../../../api/smartmelts-api/graphql/generated";
import { QuestionSectionLoadingIndicator } from "../../question/question-section-loading-indicator";
import lodash from "lodash";
import { GeneVariationPopulationCard } from "./gene-variation-population-card";
import { HeadingText } from "../../../../components/text/heading-text";
import { ContentText } from "../../../../components/text/content-text";
import { SubheadingText } from "../../../../components/text/subheading-text";

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    flex: 1,
    width: "100%",
    paddingBottom: theme.spacing(4),
  },
  textContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  geneDataTypeHeadingContainer: {
    marginBottom: theme.spacing(4),
  },
}));

interface GenesByType {
  type: GeneDataGroupType;
  genes: GenePopulationResponse[];
}

interface IGeneVariationsProps {}

export const GeneVariations: FunctionComponent<IGeneVariationsProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const { isDemo, geneData: geneDataFromContext, clearGeneData, scrollTop } = useContext(NutritionContext);

  const [geneVariationsQuery, { loading: geneLoading, data: geneData }] = useGenePopulationsByReportLazyQuery({
    onCompleted: () => {
      if (!geneDataFromContext) {
        scrollTop();
      }
    },
  });

  useEffect(() => {
    if (geneData && geneDataFromContext) {
      // @ts-ignore
      document?.getElementById(geneDataFromContext.toLowerCase())?.scrollIntoView({
        behavior: "smooth",
      });
      clearGeneData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geneData]);

  useEffect(() => {
    geneVariationsQuery({ variables: { demo: isDemo } });
  }, [geneVariationsQuery, isDemo]);

  const geneTypes = useMemo(() => {
    const genesByType: GenesByType[] = [];
    Object.values(GeneDataGroupType).map((category) => {
      const dataGroupGenes = geneData?.genePopulationsByReport
        ? geneData.genePopulationsByReport.filter((gene) => gene.geneDataGroup === category)
        : [];
      if (dataGroupGenes && !lodash.isEmpty(dataGroupGenes)) {
        genesByType.push({ type: category, genes: dataGroupGenes });
      }
      return null;
    });
    return genesByType;
  }, [geneData?.genePopulationsByReport]);

  const needsOneColumn = useMediaQuery("(max-width:674px)");

  return (
    <Grid container className={classes.container}>
      <HeadingText>{t(`gene_variations.heading`)}</HeadingText>
      <Grid container direction="column" spacing={4} alignItems="stretch">
        {!geneLoading && geneData?.genePopulationsByReport && (
          <>
            <Grid item className={classes.textContainer}>
              <ContentText light>{t(`gene_variations.subheading`)}</ContentText>
            </Grid>
            <Grid container spacing={6} item>
              {geneTypes.map((type: GenesByType, index: number) => (
                <Grid container item key={index}>
                  <Grid item xs={12} className={classes.geneDataTypeHeadingContainer} id={type.type.toLowerCase()}>
                    <SubheadingText>{t(`gene_variations.types.${type.type}`)}</SubheadingText>
                  </Grid>
                  <Grid container spacing={4} item>
                    {type.genes.map((gene, index) => (
                      <Grid item xs={12} sm={!needsOneColumn ? 6 : 12} md={12} lg={6} key={index}>
                        <GeneVariationPopulationCard
                          name={gene.geneName}
                          refsnp={gene.geneRefsnp}
                          genoType={gene.genoType}
                          population={gene.population}
                          orientation={gene.orientation}
                          references={gene.references}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </>
        )}
        {geneLoading && <QuestionSectionLoadingIndicator />}
      </Grid>
    </Grid>
  );
};
